import {Component, OnDestroy, OnInit, Input, HostListener} from '@angular/core';
import moment from 'moment';
import * as _ from 'lodash';
import {WaterReadingsService} from 'src/app/services/waterReadings.service';
import WaterReadingsModel from 'src/app/services/waterReadings.model';
import CustomerPondModel from 'src/app/services/customerPond.model';
import {CustomerPondService} from 'src/app/services/customerPond.service';
import ReadingsBatchEntriesModel from 'src/app/services/readingsBatchEntries.model';
import {ReadingsBatchEntriesService} from 'src/app/services/readingsBatchEntries.service';
import {ToastrService} from 'ngx-toastr';
import {BatchLoader} from "../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * @component BatchWaterQualityViewComponent
 * @selector app-batch-water-quality-view
 * @description Manages water quality readings and batch entries for aquaculture farm sections.
 *
 * This component provides comprehensive functionality for viewing, editing, and managing water quality
 * readings data across multiple ponds/tanks within a selected farm section. It supports batch operations,
 * pagination with infinite scroll, bulk updates to water parameters, and integration with water quality
 * monitoring systems.
 *
 * Key Features:
 * 1. Batch Water Quality Readings - Display and manage historical water quality data for batch entries
 * 2. Multi-Pond Support - Handle readings from multiple ponds in a single batch with independent edit states
 * 3. Water Parameters Management - Support for 24+ water quality parameters (pH, temperature, DO2, conductivity, etc)
 * 4. Infinite Scroll Pagination - Dynamically load batches as user scrolls down (12 items per batch)
 * 5. Bulk Parameter Updates - Edit and apply water quality parameters across selected ponds simultaneously
 * 6. Unused Ponds Integration - Add and remove readings from preparatory/unused ponds in batch entries
 * 7. Custom Parameters Support - Store and manage custom water parameters defined by users
 * 8. Form State Management - Support for view, edit, preview, and submit modes with button toggles
 * 9. Data Transformation - Parse custom parameters, format timestamps (IST conversion), deduplicate readings
 * 10. Transaction Persistence - Create/update/delete batch water quality records with validation
 *
 * Architecture:
 * - Hierarchical Data: Batch Entry → Reading Set → Pond (Tank) → Water Quality Parameters
 * - Pagination: Uses BatchLoader utility for infinite scroll with configurable batch size
 * - Data Models: Leverages WaterReadingsModel and ReadingsBatchEntriesModel for type safety
 * - State Management: Manages edit state through boolean flags (editButton, previewButton, etc)
 * - Scroll Listener: @HostListener for window:scroll event triggers batch loading
 *
 * Role-Based Behavior:
 * - All users can view water quality readings for their assigned section
 * - Users with edit permissions can modify water parameters and manage ponds
 * - Delete operations require confirmation dialog for data integrity
 *
 * Data Flow:
 * 1. Component initialization loads batch entries for selected section via geBatchWaterQualityReadings()
 * 2. User selects batch entry to view details - getBatchDetails() loads readings and pond data
 * 3. Batch readings are transformed: custom params extracted, timestamps converted to IST, deduped by tank_id
 * 4. Pond details dropdown populated with water quality parameters for each tank
 * 5. User edits water parameters in view mode with real-time value binding
 * 6. Preview mode validates data completeness before submission
 * 7. updateBatchWaterQualityReadings() prepares request body with changes and sends to API
 * 8. Response triggers success message and UI reset via ngOnInit()
 *
 * Water Parameters Supported (24 standard + custom):
 * Standard: temperature, pH, turbidity, DO2 (dissolved oxygen), conductivity, salinity, TDS, nitrite,
 * alkalinity, TAN, UAM, SG, ORP, magnesium, calcium, plankton, BOD, CO2, phosphorus, H2S gas,
 * iron, potassium, DA, sodium
 * Custom: User-defined parameters stored in cust_added_params JSON field
 *
 * @implements OnInit, OnDestroy
 * @selector app-batch-water-quality-view
 * @templateUrl ./batch-water-quality-view.component.html
 * @styleUrls ./batch-water-quality-view.component.scss
 *
 * @service ToastrService - Display success/warning/error notifications
 * @service WaterReadingsService - Fetch and update water quality readings data
 * @service ReadingsBatchEntriesService - Retrieve batch entry metadata and reading sets
 * @service CustomerPondService - Load pond details and culture data for sections
 * @service BatchTransactionsService - Manage batch transaction lifecycle
 *
 * @example
 * // Component initialization in parent component
 * <app-batch-water-quality-view
 *   [businessId]="businessId"
 *   [siteId]="farmSiteId"
 *   [sectionId]="sectionId"
 *   [businessName]="businessName">
 * </app-batch-water-quality-view>
 *
 * Data Flow Example:
 * Component Load → geBatchWaterQualityReadings(sectionId)
 *   ├─ Fetch batch entries from ReadingsBatchEntriesService
 *   ├─ Process readings: extract custom params, convert timestamps, deduplicate
 *   ├─ Initialize BatchLoader for pagination
 *   └─ Load first batch of 12 entries
 *
 * User Selects Batch → getBatchDetails(batchId, fromDate, toDate)
 *   ├─ Fetch pond details from CustomerPondService
 *   ├─ Load water readings for batch
 *   ├─ Transform data: expand custom params to main object
 *   ├─ Group parameters by type into waterQualityLogs
 *   └─ Show modal with pond details dropdown
 *
 * User Edits Parameters → onEditValueChange(pondIndex, property, value)
 *   ├─ Update specific pond's water quality log value
 *   ├─ Enable Preview button for validation
 *   └─ Track changes for batch submission
 *
 * Submit Changes → updateBatchWaterQualityReadings()
 *   ├─ Validate: pondsSelectionValidation() checks for null/empty values
 *   ├─ Transform: batchWaterQualityReadingsSetPreparation() organizes changes
 *   │  ├─ For used ponds: track only modified parameters
 *   │  ├─ For deleted ponds: add delete flag for removal
 *   │  ├─ For new ponds: group by matching parameters
 *   │  └─ Extract custom parameters to separate JSON field
 *   ├─ API Call: waterReadingsService.addBatchWaterReadings()
 *   └─ Success: Reload component and show notification
 */
@Component({
  selector: 'app-batch-water-quality-view',
  templateUrl: './batch-water-quality-view.component.html',
  styleUrls: ['./batch-water-quality-view.component.scss']
})
export class BatchWaterQualityViewComponent implements OnInit, OnDestroy {

  /**
   * @type {number}
   * @default 1
   * @description Current page number for pagination display (UI state only, not used in infinite scroll)
   */
  public currentPageNumber = 1;

  /**
   * @type {number}
   * @default 12
   * @description Number of batch entries loaded per scroll/batch (configurable pagination size)
   */
  public noOfItemsPerPage = 12;

  /**
   * @type {number}
   * @description Active page number state for pagination component binding
   */
  public activePageNo: number;

  /**
   * @type {boolean}
   * @default false
   * @description Toggle visibility of unused ponds in the batch entry form
   * @side-effect Affects pondDetails_dropDown array contents when toggled
   */
  public showOrHidePonds: boolean = false;

  /**
   * @type {boolean}
   * @description Toggle to show/hide input fields for water parameter editing
   * @side-effect Controls visibility of edit input fields in UI
   */
  public showInput: boolean;

  /**
   * @type {boolean}
   * @description Enable edit mode button and controls - allows modifying water parameters
   * @side-effect Enables onEditValueChange and showUnUsedPonds functions
   */
  public editButton: boolean;

  /**
   * @type {boolean}
   * @description Show cancel button to revert changes without saving
   * @side-effect Triggers cancelWaterQualityReadingsUpdate() when clicked
   */
  public cancelButton: boolean;

  /**
   * @type {boolean}
   * @description Enable submit button after preview validation passes
   * @side-effect Triggers updateBatchWaterQualityReadings() when clicked
   */
  public submitButton: boolean;

  /**
   * @type {boolean}
   * @description Enable preview button to validate data before submission
   * @side-effect Triggers previewBatchWaterQualityReadings() and enables submitButton
   */
  public previewButton: boolean;

  /**
   * @type {boolean}
   * @default false
   * @description Disable submit button during API request to prevent duplicate submissions
   * @side-effect Set to true during updateBatchWaterQualityReadings, reset on error/success
   */
  public isCommitDisabled: boolean;

  /**
   * @type {string}
   * @description Logged-in user's unique identifier from localStorage
   * @default localStorage.getItem('loginPersonId')
   * @side-effect Used for tracking user who made changes
   */
  public loginPersonId: string;

  /**
   * @type {Array<any>}
   * @description Array of water quality readings for currently selected batch entry
   * Contains individual readings per tank with all water parameters and custom parameters expanded
   * @side-effect Modified by getBatchDetails, onEditValueChange, cancelWaterQualityReadingsUpdate
   */
  public waterReadingsLogs: Array<any>;

  /**
   * @type {WaterReadingsModel}
   * @description Model instance for water quality readings transaction
   * Used to prepare and submit batch water quality reading updates
   * @default new WaterReadingsModel()
   */
  public waterReadingsModel: WaterReadingsModel;

  /**
   * @type {Array<ReadingsBatchEntriesModel>}
   * @description Paginated list of batch water quality entries currently displayed
   * Updated by BatchLoader as user scrolls
   * @side-effect Modified by geBatchWaterQualityReadings, BatchLoader.loadNextBatch
   */
  public waterQualityBatchReadingsList: Array<ReadingsBatchEntriesModel>;

  /**
   * @type {Array<ReadingsBatchEntriesModel>}
   * @description Complete list of all batch water quality entries for the section
   * Used as source data by BatchLoader for pagination
   * @side-effect Modified by geBatchWaterQualityReadings only
   */
  public waterQualityBatchReadingsListData: Array<ReadingsBatchEntriesModel>;

  /**
   * @type {WaterReadingsModel}
   * @description Water batch transaction model for API submission
   * Contains batch entry ID, ponds array, custom parameters, and submission action (update/delete)
   * @default new WaterReadingsModel()
   */
  public waterBatchTransaction: WaterReadingsModel;

  /**
   * @type {Array<CustomerPondModel>}
   * @description All ponds (tanks) in the selected section with culture data
   * Loaded by getBatchDetails from CustomerPondService
   * Used to populate unused ponds list
   */
  public customerPonds: Array<CustomerPondModel>;

  /**
   * @type {any}
   * @description Formatted date range string for selected batch entry (e.g., "Jan 15, 2026 - Jan 20, 2026")
   * Set by getBatchDetails from batch entry readings date range
   * @side-effect Used in updateBatchWaterQualityReadings for API submission
   */
  public selectedBatchTimeStamp: any;

  /**
   * @type {any[]}
   * @default []
   * @description Main dropdown array containing pond details with editable water quality logs
   * Each entry has: pond_name, tank_id, waterQualityLogs (water parameters), pondUsed flag
   * @side-effect Modified by: getBatchDetails, showUnUsedPonds, onEditValueChange, removePond, cancelWaterQualityReadingsUpdate
   */
  public pondDetails_dropDown: any[] = [];

  /**
   * @type {any[]}
   * @default []
   * @description Duplicate/backup copy of pondDetails_dropDown for cancel/revert functionality
   * Created by JSON.parse(JSON.stringify()) to maintain deep copy
   * @side-effect Used by cancelWaterQualityReadingsUpdate to restore original state
   */
  public dupPondDetails_dropDown: any[] = [];

  /**
   * @type {string}
   * @description ID of currently selected batch entry for editing
   * Set by getBatchDetails, used in showUnUsedPonds and updateBatchWaterQualityReadings
   */
  public batchEntryId: string;

  /**
   * @type {string}
   * @Input
   * @description Business entity ID passed from parent component
   * Used to identify user's business context for authorization
   */
  @Input('businessId') public businessId: string;

  /**
   * @type {string}
   * @Input
   * @description Farm site ID for which water quality readings are displayed
   * Identifies specific aquaculture farm location
   */
  @Input('siteId') public farmSiteId: string;

  /**
   * @type {string}
   * @Input
   * @description Section/pond section ID within farm site
   * Used to filter batch entries and pond details
   */
  @Input('sectionId') public sectionId: string;

  /**
   * @type {string}
   * @Input
   * @description Business name displayed in UI header
   * Retrieved from parent component or localStorage
   */
  @Input('businessName') public businessName: string;

  /**
   * @type {boolean}
   * @default false
   * @description Show/hide loading spinner while fetching batch entries from API
   * @side-effect Set by BatchLoader callback during loadNextBatch
   */
  public listLoader = false;

  /**
   * @type {number}
   * @default 12
   * @description Number of records to load per batch during infinite scroll
   * Configurable batch size for pagination
   */
  public recordsPerBatch = 12;

  /**
   * @type {number}
   * @default 0
   * @description Current index for records loaded so far
   * Tracks pagination progress through total list
   */
  public startIndex = 0;

  /**
   * @type {boolean}
   * @default false
   * @description Flag indicating if list is currently filtered (search/filter mode)
   * When true, prevents loading more items until filter is cleared
   */
  public isFiltering: boolean = false;

  /**
   * @type {boolean}
   * @default false
   * @description Flag indicating if no batch entries exist for current section
   * Set by BatchLoader when filtered/loaded list is empty
   */
  public noList: boolean = false;

  /**
   * @type {BatchLoader<any>}
   * @description Infinite scroll pagination utility instance
   * Manages batch loading, state, and callback execution
   * @side-effect Initialized in initializeBatchLoader(), used in onScroll and checkScrollAndLoad
   */
  private batchLoader: BatchLoader<any>;


  /**
   * @method constructor
   * @description Initialize component with required service dependencies and model instances
   *
   * @service ToastrService - Notification service for displaying success/error messages to users
   * @service CustomerPondService - Service for loading pond and culture details from API
   * @service BatchTransactionsService - Service for managing batch transaction lifecycle and metadata
   * @service WaterReadingsService - Service for fetching and updating water quality readings
   * @service ReadingsBatchEntriesService - Service for retrieving batch entry metadata and reading sets
   *
   * @param {ToastrService} toasterService - Injectable for displaying notifications
   * @param {CustomerPondService} customerPondService - Injectable for pond data operations
   * @param {BatchTransactionsService} batchTransactionService - Injectable for batch transaction operations
   * @param {WaterReadingsService} waterQualityReadingsService - Injectable for water readings (batch view)
   * @param {ReadingsBatchEntriesService} waterBatchReadingsService - Injectable for batch entries metadata
   * @param {WaterReadingsService} waterReadingsService - Injectable for water readings (submit operations)
   *
   * @side-effect
   * - Initializes three WaterReadingsModel instances (waterReadingsModel, waterBatchTransaction, waterReadingsModel)
   * - Services are stored as class properties for use throughout component lifecycle
   */
  constructor(private toasterService: ToastrService, public customerPondService: CustomerPondService, public waterQualityReadingsService: WaterReadingsService, public waterBatchReadingsService: ReadingsBatchEntriesService, public waterReadingsService: WaterReadingsService) {
    this.waterReadingsModel = new WaterReadingsModel();
    this.waterBatchTransaction = new WaterReadingsModel();
    this.waterReadingsModel = new WaterReadingsModel();
  }

  /**
   * @method ngOnInit
   * @lifecycle angular
   * @async
   * @description Initialize component state and load batch water quality readings for current section
   * @returns {void}
   * @side-effect Resets UI state flags, loads batch data from API, initializes pagination
   */
  public ngOnInit(): void {
    window.scrollTo(0, 0);
    this.isFiltering = false;
    this.waterQualityBatchReadingsListData = [];
    this.waterQualityBatchReadingsList = [];
    this.editButton = false;
    this.submitButton = false;
    this.previewButton = false;
    this.cancelButton = false;
    this.isCommitDisabled = false;
    this.showInput = false;
    this.businessName = localStorage.getItem('loginBusinessName');
    this.loginPersonId = localStorage.getItem('loginPersonId');
    this.geBatchWaterQualityReadings(this.sectionId);
  }

  /**
   * @method ngOnDestroy
   * @lifecycle angular
   * @description Cancel all pending HTTP requests when component is destroyed
   * @returns {void}
   * @side-effect Aborts all in-flight HTTP requests to prevent memory leaks
   */
  public ngOnDestroy(): void {
    this.customerPondService.cancelOnGoingHttpRequests();
    this.waterQualityReadingsService.cancelOnGoingHttpRequests();
  }

  /**
   * @method geBatchWaterQualityReadings
   * @async
   * @description Load all batch water quality readings for specified section and prepare data for pagination
   *
   * Data Processing:
   * - Fetches batch entries from ReadingsBatchEntriesService API
   * - For each batch entry processes readings array:
   *   1. Extract individual reading timestamps and convert to Date objects
   *   2. Calculate date range (min/max) for batch entry
   *   3. Format date range as readable string (e.g., "Jan 15, 2026 - Jan 20, 2026")
   *   4. Convert UTC createdAt timestamp to IST (UTC+5:30)
   *   5. Deduplicate readings by tank_id (keep first occurrence)
   * - Initialize and reset BatchLoader utility for infinite scroll
   * - Load first batch of entries (12 records default)
   *
   * @param {string} sectionId - Section identifier to filter batch entries
   * @returns {Promise<void>}
   * @side-effect Populates waterQualityBatchReadingsListData, initializes BatchLoader pagination
   */
  public async geBatchWaterQualityReadings(sectionId: string) {
    this.waterQualityBatchReadingsList = [];
    this.isFiltering = false;
    try {
      this.waterQualityBatchReadingsListData = await this.waterBatchReadingsService.getBatchReadingsList(sectionId);
      if (this.waterQualityBatchReadingsListData && this.waterQualityBatchReadingsListData.length > 0) {
        this.waterQualityBatchReadingsListData.forEach((batchEntry: any) => {
          let datedObjects = batchEntry.Readings.map((date: any) => new Date(date.time));
          // Find the minimum and maximum dates
          const minDate = new Date(Math.min(...datedObjects));
          const maxDate = new Date(Math.max(...datedObjects));
          // Format dates to YYYY-MM-DD string format
          const formatDate = (date: any) => {
            const options = {month: 'short', day: 'numeric', year: 'numeric'};
            return date.toLocaleString('en-US', options);
          };
          batchEntry.from_Date = formatDate(minDate);
          batchEntry.to_Date = formatDate(maxDate);
          const utcTime = batchEntry.createdAt; // UTC time in ISO 8601 format
          // Create a Date object from the UTC time string
          const utcDate = new Date(utcTime);
          // Add 5 hours and 30 minutes to the UTC date
          utcDate.setUTCHours(utcDate.getUTCHours() + 5);
          utcDate.setUTCMinutes(utcDate.getUTCMinutes() + 30);
          // Get the updated UTC time in ISO 8601 format
          const updatedUtcTime = utcDate.toISOString();
          batchEntry.createdAt = updatedUtcTime;
          batchEntry.Readings = batchEntry.Readings.filter((element: any, index: any) => batchEntry.Readings.findIndex((Object: any) => Object.tank_id === element.tank_id) === index);
        });
      }
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      if (this.waterQualityBatchReadingsListData.length == 0) {
        this.toasterService.warning('Batch data not available.');
      }
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method onScroll
   * @hostListener window:scroll
   * @description Detect window scroll events and trigger batch loading on infinite scroll
   * @returns {void}
   * @side-effect Triggers checkScrollAndLoad method when user scrolls
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * @type {any}
   * @private
   * @description Timeout ID for debouncing scroll events
   */
  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Check if user scrolled to bottom and load next batch of entries
   *
   * Logic:
   * 1. Clear any pending scroll timeout to debounce rapid scroll events (50ms delay)
   * 2. Check if scroll position reached near bottom (100px threshold)
   * 3. Verify BatchLoader is not already loading to prevent duplicate requests
   * 4. Verify not in filtering mode (prevents loading during search)
   * 5. If all conditions met, call loadNextBatch() to fetch next 12 entries
   *
   * @returns {void}
   * @side-effect Clears scrollTimeout, sets new timeout, calls batchLoader.loadNextBatch
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 &&
        !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Create and configure BatchLoader instance for infinite scroll pagination
   * @returns {void}
   * @side-effect Initializes batchLoader instance with callbacks for list updates and loading state
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.waterQualityBatchReadingsList : this.waterQualityBatchReadingsListData,
      (updatedList) => {
        this.waterQualityBatchReadingsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method getBatchDetails
   * @async
   * @description Load water quality readings and pond details for selected batch entry
   *
   * Execution flow:
   * 1. Reset all UI state flags (editButton, cancelButton, previewButton, submitButton, isCommitDisabled)
   * 2. Store selected batch entry ID and formatted date range from/to
   * 3. Load all ponds for section via CustomerPondService
   * 4. Extract readings for selected batch from waterQualityBatchReadingsList
   * 5. Transform readings array:
   *    - Parse cust_added_params JSON string to individual key-value pairs
   *    - Expand custom parameters to main object level
   * 6. Extract water quality parameters into separate waterQualityLogs object for each reading
   * 7. Build pondDetails_dropDown array with formatted pond data and waterQualityLogs
   * 8. Create deep copy of pondDetails_dropDown for cancel/revert functionality
   * 9. Show modal dialog with pond details or warning if no readings
   *
   * @param {string} batchEntryId - Unique identifier of batch entry to load
   * @param {any} fromdate - Start date of batch readings (formatted)
   * @param {any} toDate - End date of batch readings (formatted)
   * @returns {Promise<void>}
   * @side-effect Populates waterReadingsLogs, pondDetails_dropDown, dupPondDetails_dropDown; shows modal
   */
  public async getBatchDetails(batchEntryId: string, fromdate: any, toDate: any) {
    try {
      this.editButton = false;
      this.cancelButton = false;
      this.previewButton = false;
      this.submitButton = false;
      this.isCommitDisabled = false;
      this.batchEntryId = batchEntryId;
      this.showInput = false;
      this.showOrHidePonds = false;
      this.dupPondDetails_dropDown = [];
      this.customerPonds = [];
      const waterParameters = ['temperature', 'pH', 'turbidity', 'do2', 'conductivity', 'salinity', 'tds', 'nitrite', 'alkalinity', 'tan', 'uam', 'sg', 'orp', 'magnesium', 'calcium', 'plankton', 'bod', 'co2', 'phosphorus', 'h2sGas', 'iron', 'potassium', 'da', 'sodium'];
      this.selectedBatchTimeStamp = fromdate + ' -  ' + toDate;
      this.customerPonds = await this.customerPondService.getAllPondsBySectionId(this.sectionId);
      this.waterReadingsLogs = this.waterQualityBatchReadingsList.filter((batch: ReadingsBatchEntriesModel) => batch.id === batchEntryId)[0].Readings;
      // Extracting the objects in the cust_added_params key to the outer loop objecct for each object in waterReadingsLogs data
      this.waterReadingsLogs = this.waterReadingsLogs.map(obj => {
        const {cust_added_params, ...rest} = obj;
        const custAddedParamsArray = JSON.parse(cust_added_params);
        custAddedParamsArray.forEach((item: any) => rest[item.key] = item.value);
        return rest;
      });
      // creating an waterQualityLogs Object for each pond object and values are binded by comapring waterParameters
      this.waterReadingsLogs.forEach(obj => {
        obj.waterQualityLogs = {};
        Object.keys(obj).forEach(key => {
          if (waterParameters.includes(key)) {
            obj.waterQualityLogs[key] = obj[key];
          }
        });
      });
      // Extracting the certain keys from waterReadingsLogs and assigning it to the pondDetails_dropDown array
      this.pondDetails_dropDown = this.waterReadingsLogs.reduce((acc, eachrec) => {
        acc.push({
          id: eachrec.id,
          tank_id: eachrec.tank_id,
          pond_name: eachrec.pond.name,
          pond_culture_id: eachrec.pond_culture_id,
          water_type_id: eachrec.water_type_id,
          soil_type_id: eachrec.soil_type_id,
          isPreparatory: eachrec.isPreparatory,
          device_id: null,
          tank_number: eachrec.tank_number,
          waterQualityLogs: eachrec.waterQualityLogs,
          pondUsed: true
        });
        return acc;
      }, []);
      // Creating a deep copy of array1 using spread operator with map
      this.dupPondDetails_dropDown = JSON.parse(JSON.stringify(this.pondsSelectionValidation()));
      // checking pond details length > 0 or not to show or hide popup
      if (this.pondDetails_dropDown.length == 0) {
        this.toasterService.warning('Water Quality Readings are not available');
        $('#addModal').modal('hide');
      } else {
        $('#myModal').modal('show');
      }
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method convertToKgs
   * @description Convert grams to kilograms with 3 decimal places
   * @param {any} grams - Weight value in grams
   * @returns {string} Weight formatted as "X.XXX Kgs" (e.g., "1.500 Kgs")
   */
  public convertToKgs = (grams: any) => {
    const kgsValue = grams / 1000;
    return kgsValue.toFixed(3) + ' Kgs';
  };

  /**
   * @method convertTo24Hrs
   * @description Convert 24-hour time format to 12-hour AM/PM format using Moment.js
   * @param {any} timeStamp - ISO 8601 timestamp string (e.g., "2026-01-19T14:30:00Z")
   * @returns {string} 12-hour formatted time (e.g., "2:30 PM")
   */
  public convertTo24Hrs = (timeStamp: any) => {
    const time = timeStamp.split('T')[1];
    const _12hrs = moment(time, ['HH:mm']).format('h:mm A');
    return _12hrs;
  };

  /**
   * @method validateNumber
   * @description Validate numeric input for water quality parameter fields
   *
   * Validation rules:
   * 1. Allow: digits 0-9, decimal point (.), Backspace
   * 2. Prevent: Letters, special characters except above
   * 3. Prevent: Multiple dots in single number (only one decimal allowed)
   * 4. Prevent: Dot as first character (must start with number)
   *
   * Use case: Restricts input in water parameter fields to valid numeric values with optional decimal
   *
   * @param {any} event - Keyboard event from input field
   * @returns {void}
   * @side-effect Calls event.preventDefault() to block invalid keystrokes
   */
  public validateNumber(event: any) {
    const key = event.key;
    const value = event.target.value;
    // Allow numbers, the dot (decimal point), and the Backspace key
    if (
      !/^\d$/.test(key) && // Check if the entered character is a number
      key !== '.' && // Check if the entered character is a dot (decimal point)
      key !== 'Backspace' // Check if the Backspace key was pressed
    ) {
      event.preventDefault();
    }

    // Allow only one dot (decimal point)
    if (key === '.' && value.includes('.')) {
      event.preventDefault();
    }

    // Disallow the first letter being a dot
    if (key === '.' && value === '') {
      event.preventDefault();
    }
  }

  /**
   * @method showUnUsedPonds
   * @description Toggle visibility of unused ponds in batch entry and add/remove from dropdown
   *
   * Execution flow:
   * 1. Filter all ponds in section to find those without readings in current batch
   * 2. Excluded from result: Preparatory ponds, ponds without active culture data, ponds with existing readings
   * 3. For each unused pond, create new entry with empty waterQualityLogs object
   * 4. When type='showPonds': Append unused ponds to pondDetails_dropDown
   * 5. When type='hidePonds': Remove unused ponds from dropdown
   * 6. Update showOrHidePonds flag to track current state
   *
   * @param {string} type - 'showPonds' to add unused ponds or 'hidePonds' to remove them
   * @returns {void}
   * @side-effect
   * - Modifies pondDetails_dropDown by appending or filtering pond entries
   * - Updates showOrHidePonds boolean flag
   */
  public showUnUsedPonds(type: string) {
    try {
      const waterparameters = ['pH', 'salinity', 'alkalinity', 'turbidity', 'tan', 'temperature', 'uam', 'sg', 'do2', 'conductivity', 'tds', 'nitrite', 'orp', 'magnesium', 'calcium', 'biofloc', 'carbonates', 'bicarbonates', 'hardness', 'ammonia', 'plankton', 'bod', 'co2', 'phosphorus', 'h2sGas', 'iron', 'potassium', 'da', 'sodium'];
      let pondDetaisUnUsed: any[] = [];
      if (this.customerPonds && this.customerPonds.length > 0) {
        pondDetaisUnUsed = this.customerPonds.reduce((acc, eachrec) => {
          if (!eachrec.isPreparatory && eachrec.PondCultureData && eachrec.PondCultureData.length > 0) {
            eachrec.PondCultureData.forEach(pcd => {
              if (!pcd.end_date) {
                acc.push({
                  pond_name: eachrec.name,
                  tank_id: pcd.pond_id,
                  pond_culture_id: pcd.id,
                  water_type_id: pcd.water_type_id,
                  soil_type_id: pcd.soil_type_id,
                  isPreparatory: eachrec.isPreparatory,
                  device_id: null,
                  tank_number: eachrec.pond_code,
                  batch_entry_id: this.batchEntryId,
                  waterQualityLogs: waterparameters.reduce((acc, param) => ({...acc, [param]: null}), {}),
                });
              }
            });
          }
          return acc;
        }, []).filter((obj: any) => {
          const pondIds = _.map(this.waterReadingsLogs, 'tank_id');
          if (!pondIds.includes(obj.tank_id)) {
            obj.pondUsed = false;
            return obj;
          }
        });
      }
      if (type == 'showPonds') {
        this.showOrHidePonds = true;
        this.pondDetails_dropDown = [...this.pondDetails_dropDown, ...pondDetaisUnUsed];
      } else {
        // Filtering elements from pondDetails_dropDown that are not present in pondDetaisUnUsed
        this.pondDetails_dropDown = this.pondDetails_dropDown.filter(item => !pondDetaisUnUsed.some(unusedItem => unusedItem.tank_id === item.tank_id));
        this.showOrHidePonds = false;
      }
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method onEditValueChange
   * @description Update water quality parameter value for specific pond when user edits field
   *
   * Real-time binding of edited values to waterQualityLogs object for selected pond.
   * This method is called on every change event from input fields, allowing instant updates
   * without form submission.
   *
   * @param {number} pondIndex - Array index of pond in pondDetails_dropDown
   * @param {string} property - Name of water quality parameter being edited (e.g., "pH", "temperature")
   * @param {any} value - New value entered by user for the water quality parameter
   * @returns {void}
   * @side-effect
   * - Updates specific property in pondDetails_dropDown[pondIndex].waterQualityLogs
   * - Does not affect other ponds' values
   */
  public onEditValueChange(pondIndex: number, property: string, value: any) {
    // Update the specific pond's property with the edited value
    this.pondDetails_dropDown[pondIndex].waterQualityLogs[property] = value;
  }

  /**
   * @method removePond
   * @description Remove pond card from batch entry with confirmation dialog
   *
   * Execution flow:
   * 1. Check if this is the last remaining pond in batch
   *    - If yes: Show confirmation "Are you sure you want to delete the batch"
   *    - If user confirms: Remove pond and enable submitButton (deletion will be sent to API)
   * 2. If multiple ponds exist:
   *    - Show confirmation "Are you sure you want to delete a pond"
   *    - If user confirms: Remove pond from array
   *
   * @param {number} i - Array index of pond to remove from pondDetails_dropDown
   * @returns {boolean|void} Returns true if pond was deleted, void if operation was cancelled
   * @side-effect
   * - Removes element from pondDetails_dropDown array via splice
   * - Sets submitButton=true when removing pond (triggers API submission)
   * - Requires user confirmation before deletion
   */
  public removePond(i: number) {
    if (this.pondDetails_dropDown.length == 1) {
      const batchDeletion = confirm('Are u sure you want to delete the batch');
      if (batchDeletion) {
        this.pondDetails_dropDown.splice(i, 1);
        this.submitButton = true;
        this.previewButton = false;
        this.editButton = false;
      }
    } else {
      const pondDeletion = confirm('Are you sure you want to delete a pond');
      if (pondDeletion) {
        this.pondDetails_dropDown.splice(i, 1);
        return true;
      }
    }
  }

  /**
   * @method enablingInputFieldsForUpdate
   * @description Enable edit mode and show input fields for water parameter modification
   *
   * UI state transition: View Mode → Edit Mode
   * - Shows input fields for editing (showInput=true)
   * - Hides edit button, shows cancel and preview buttons
   * - Disables submit button until preview passes validation
   *
   * @returns {void}
   * @side-effect Updates UI state flags to enable edit mode
   */
  public enablingInputFieldsForUpdate() {
    this.showInput = true;
    this.editButton = false;
    this.cancelButton = true;
    this.previewButton = true;
    this.submitButton = false;
  }

  /**
   * @method cancelWaterQualityReadingsUpdate
   * @description Cancel edit mode and revert all changes to original water parameter values
   *
   * Execution flow:
   * 1. Restore pondDetails_dropDown from dupPondDetails_dropDown backup (deep copy)
   * 2. Hide input fields (showInput=false)
   * 3. Hide unused ponds if they were added (showOrHidePonds=false)
   * 4. Show edit button, hide cancel/preview/submit buttons
   *
   * @returns {void}
   * @side-effect Reverts pondDetails_dropDown to original state, updates UI state flags
   */
  public cancelWaterQualityReadingsUpdate() {
    this.showInput = false;
    this.showOrHidePonds = false;
    this.editButton = true;
    this.previewButton = false;
    this.cancelButton = false;
    this.submitButton = false;
    this.pondDetails_dropDown.splice(0, this.pondDetails_dropDown.length, ...this.dupPondDetails_dropDown);
  }

  /**
   * @method previewBatchWaterQualityReadings
   * @description Transition from edit mode to preview mode before final submission
   *
   * UI state transition: Edit Mode → Preview Mode
   * - Hides input fields (showInput=false)
   * - Enables submit button for final confirmation
   * - Hides preview and cancel buttons
   * - Shows edit button for returning to edit mode if needed
   *
   * @returns {void}
   * @side-effect Updates UI state flags for preview mode
   */
  public previewBatchWaterQualityReadings() {
    this.showInput = false;
    this.previewButton = false;
    this.submitButton = true;
    this.editButton = true;
    this.cancelButton = false;
  }

  /**
   * @method pondsSelectionValidation
   * @description Validate that ponds have water quality parameters entered and return filtered list
   *
   * Validation logic:
   * 1. Iterate through pondDetails_dropDown array
   * 2. For each pond, clean waterQualityLogs object by removing null/empty string values
   * 3. Filter to return only ponds with at least one valid water parameter
   * 4. Returns empty array if no valid parameters found in any pond
   *
   * Used to ensure no empty water quality records are submitted to API
   *
   * @returns {Array} Array of ponds with valid water quality parameters
   * @side-effect None (does not modify component state, only returns filtered array)
   */
  public pondsSelectionValidation() {
    try {
      if (this.pondDetails_dropDown.length > 0) {
        const filteredPonds = this.pondDetails_dropDown.map((item) => {
          const waterQualityLogs = {...item.waterQualityLogs};
          Object.keys(waterQualityLogs).forEach((key) => {
            if (waterQualityLogs[key] === '' || waterQualityLogs[key] === null) {
              delete waterQualityLogs[key];
            }
          });
          return {...item, waterQualityLogs};
        }).filter((item) => Object.keys(item.waterQualityLogs).length > 0);
        return filteredPonds;
      } else {
        return [];
      }
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
    }
  }

  /**
   * @method batchWaterQualityReadingsSetPreparation
   * @description Transform edited water quality readings into API submission request body format
   *
   * Execution flow:
   * 1. Get validated ponds from pondsSelectionValidation()
   * 2. For used ponds (existing readings):
   *    - Compare waterQualityLogs with original dupPondDetails to find changes
   *    - Only include ponds with modified values
   *    - Extract custom parameters (magnesium, calcium, plankton, etc) into cust_added_params JSON
   * 3. For deleted ponds (in dupPondDetails but not in ponds):
   *    - Mark with delete=true flag for API to remove
   * 4. For new/unused ponds (pondUsed=false):
   *    - Group by matching waterQualityLogs (consolidate similar readings)
   *    - Create ponds array with all tank IDs for grouped reading
   *    - Extract custom parameters to separate JSON field
   * 5. Return array of all changes (updates, deletes, additions) ready for API
   *
   * @returns {Array} Array of request objects for API submission
   * @side-effect None (does not modify component state)
   */
  public batchWaterQualityReadingsSetPreparation() {
    // Define static custom parameter names"
    const customParameterNames = ['magnesium', 'calcium', 'plankton', 'bod', 'co2', 'phosphorus', 'h2sGas', 'iron', 'potassium', 'da', 'sodium'];
    // Getting ponds details updated data by removing waterQualityLogs keys by checking that the key value contains null or empty string values
    const ponds = this.pondsSelectionValidation();
    // looping used and un used ponds details for creating a req body
    if (ponds.length > 0) {
      let newUsedArray = []; // initializing the empty variable for final array
      // for used Ponds Array preparation
      for (const usedPonds of this.dupPondDetails_dropDown) {
        const pondsIds = _.map(ponds, 'tank_id');
        if (pondsIds.includes(usedPonds.tank_id)) {
          const pondObject = ponds.find((item) => item.tank_id === usedPonds.tank_id);
          const diffKeys = Object.keys(usedPonds.waterQualityLogs).filter(
            (key) => usedPonds.waterQualityLogs[key] !== pondObject.waterQualityLogs[key]
          );
          if (diffKeys.length > 0) {
            const newObj = {...pondObject, ...pondObject.waterQualityLogs};
            delete newObj.waterQualityLogs;
            // Filter the custom parameters from the input data
            const customParams = Object.keys(newObj)
              .filter(key => customParameterNames.includes(key))
              .map(key => ({key: `${key}`, value: newObj[key]}));
            // Remove the custom parameters from the top-level object
            Object.keys(newObj).forEach((paramName: string) => {
              if (customParameterNames.includes(paramName)) {
                delete newObj[paramName];
              }
            });
            newObj.cust_added_params = JSON.stringify(customParams);
            ;
            delete newObj.pondUsed;
            newUsedArray.push(newObj);
          }
        } else {
          const newObj = {...usedPonds, delete: true};
          delete newObj.pond_name;
          const {
            waterQualityLogs,
            device_id,
            isPreparatory,
            pondUsed,
            pond_culture_id,
            soil_type_id,
            water_type_id,
            tank_id,
            tank_number,
            ...filteredObj
          } = newObj;
          newUsedArray.push(filteredObj);
        }
      }
      // For UnUsed Ponds Array preparation
      const groupedObjects = ponds.reduce((acc, obj) => {
        if (!obj.pondUsed && Object.keys(obj.waterQualityLogs).length > 0) {
          const waterQualityLogsKey = JSON.stringify(obj.waterQualityLogs);
          if (!acc.hasOwnProperty(waterQualityLogsKey)) {
            acc[waterQualityLogsKey] = {...obj.waterQualityLogs, ponds: []};
          }

          // Filter the custom parameters from the input data
          const customParams = Object.keys(obj.waterQualityLogs)
            .filter(key => customParameterNames.includes(key))
            .map(key => ({key, value: obj.waterQualityLogs[key]}));

          // Remove the custom parameters from the top-level object
          customParameterNames.forEach(paramName => {
            if (acc[waterQualityLogsKey].hasOwnProperty(paramName)) {
              delete acc[waterQualityLogsKey][paramName];
            }
          });

          const {tank_id, pond_culture_id, water_type_id, soil_type_id, isPreparatory, device_id, tank_number} = obj;
          acc[waterQualityLogsKey].ponds.push({
            tank_id,
            pond_culture_id,
            water_type_id,
            soil_type_id,
            isPreparatory,
            device_id,
            tank_number
          });
          acc[waterQualityLogsKey].cust_added_params = JSON.stringify(customParams);
          acc[waterQualityLogsKey].batch_entry_id = obj.batch_entry_id;
        }
        return acc;
      }, {});
      newUsedArray = [...newUsedArray, ...Object.values(groupedObjects)];
      return newUsedArray;
    } else {
      return [];
    }
  }

  /**
   * @method updateBatchWaterQualityReadings
   * @async
   * @description Submit water quality reading changes (updates/deletes) to API for persistence
   *
   * Execution flow:
   * 1. Disable submit button to prevent duplicate submissions (isCommitDisabled=true)
   * 2. Check if changes exist via batchWaterQualityReadingsSetPreparation()
   * 3. If changes exist:
   *    - Build waterBatchTransaction object with: action='update', farm/section IDs, date range, readingSets
   * 4. If no changes (all ponds deleted):
   *    - Build waterBatchTransaction object with: action='delete', batch_entry_id for deletion
   * 5. Submit via waterReadingsService.addBatchWaterReadings()
   * 6. On success: Show success toast, close modal, reload component via ngOnInit()
   * 7. On error: Show error toast, re-enable submit button
   *
   * @returns {Promise<void>}
   * @side-effect
   * - Submits API request to persist water quality reading changes
   * - Reloads component on success or enables submit button on failure
   * - Shows toast notifications for user feedback
   */
  public async updateBatchWaterQualityReadings() {
    try {
      this.isCommitDisabled = true;
      //water quality readings batch transactions Req body
      if (this.batchWaterQualityReadingsSetPreparation().length > 0) {
        this.waterBatchTransaction.to = 'update';
        this.waterBatchTransaction.farm_site_id = this.farmSiteId;
        this.waterBatchTransaction.section_id = Number(this.sectionId);
        this.waterBatchTransaction.batch_type = 'Water';
        this.waterBatchTransaction.fromDate = this.selectedBatchTimeStamp.from_Date;
        this.waterBatchTransaction.toDate = this.selectedBatchTimeStamp.to_Date;
        this.waterBatchTransaction.time = this.waterBatchTransaction.toDate;
        this.waterBatchTransaction.readingSets = this.batchWaterQualityReadingsSetPreparation();
      } else {
        this.waterBatchTransaction.to = 'delete';
        this.waterBatchTransaction.batch_entry_id = Number(this.batchEntryId);
      }
      const batchReadingUpdated = await this.waterReadingsService.addBatchWaterReadings(this.waterBatchTransaction);
      if (batchReadingUpdated.hybridUpdate || batchReadingUpdated.deleted > 1) {
        this.toasterService.success('Batch Water Readings updated successfully', '', {
          timeOut: 3000
        });
        $('#myModal').modal('hide');
        this.ngOnInit();
      } else {
        this.toasterService.error('Batch Water Readings update failed, Please try again');
        this.isCommitDisabled = false;
      }
    } catch (error) {
      this.toasterService.error(error?.error?.message || error?.message || error);
      this.isCommitDisabled = false;
    }
  }

  /**
   * @method pageChange
   * @description Handle pagination page change event from pagination component
   *
   * Updates activePageNo to reflect current page selection in UI.
   * Note: This component uses infinite scroll via BatchLoader, so this is for UI state tracking only.
   *
   * @param {number} pageNum - Page number selected by user
   * @returns {void}
   * @side-effect Updates activePageNo property for pagination UI binding
   */
  public pageChange(pageNum: number) {
    this.activePageNo = pageNum;
  }
}
